import { singleton } from 'tsyringe';
import type { SupportedCurrency } from './CurrencyConstants';
import { CURRENCY_SYMBOLS, CURRENCY_NAMES, DEFAULT_CURRENCY } from './CurrencyConstants';

/**
 * Service for providing currency information
 * 
 * Uses hardcoded currency constants for symbols, names, and supported currencies.
 */
@singleton()
export class CurrencyService {
    private _symbols: Record<string, string> = { ...CURRENCY_SYMBOLS };
    private _names: Record<string, string> = { ...CURRENCY_NAMES };
    private _currencies: string[] = Object.keys(CURRENCY_SYMBOLS);

    /**
     * Get the symbol for a currency code
     * @param code Currency code (e.g., 'USD')
     * @returns Currency symbol (e.g., '$')
     */
    getSymbol(code: string): string {
        return this._symbols[code] || '$';
    }

    /**
     * Get the display name for a currency code
     * @param code Currency code (e.g., 'USD')
     * @returns Currency name (e.g., 'United States Dollar')
     */
    getName(code: string): string {
        return this._names[code] || code;
    }

    /**
     * Get all supported currency codes
     * @returns Array of currency codes
     */
    getCurrencies(): string[] {
        return [...this._currencies];
    }

    /**
     * Get all currency symbols as a map
     * @returns Record mapping currency codes to symbols
     */
    getSymbols(): Record<string, string> {
        return { ...this._symbols };
    }

    /**
     * Get all currency names as a map
     * @returns Record mapping currency codes to names
     */
    getNames(): Record<string, string> {
        return { ...this._names };
    }

    /**
     * Get the default currency code
     * @returns Default currency code
     */
    getDefaultCurrency(): SupportedCurrency {
        return DEFAULT_CURRENCY;
    }

    /**
     * Check if a currency code is supported
     * @param code Currency code to check
     * @returns True if currency is supported
     */
    isSupported(code: string): boolean {
        return this._currencies.includes(code);
    }
}
